import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import MotifTableHeaderRenderer from '@ey/motif-react-wrapper/Table';
import styled from 'styled-components';
import {
	getAccountsAllIds,
	getAccountsByAccountTypeIds,
	getSliceById,
	getTaskDocumentByDocumentId
} from '../../../../selectors/Ares/selectors';
import Utility from '../../../../util/utilityfunctions';
import {AccountType} from '../../../../util/uiconstants';
import {labels, getPriorPeriodChange, isNullOrUndefined} from '../../../../util/utils';
import MotifTableWrapper from '../../BaseComponentWrappers/MotifTableWrapper';
import {
	AccountNameCellRender,
	CurrentYearBalanceCellRender,
	DesignationCellRender,
	HasEstimatesCellRender,
	PriorPeriodCellRender,
	AnalyticsCellRender,
	XMaterialityCellRender
} from './AccountSummaryConfig';
import EditAccountModalV2 from '../EditAccount/EditAccountModalV2';
import {getShownModalEntityId, useDocumentId, useModal, useUserPreferences} from '../../../../util/customHooks';
import Button from '../../BaseComponentWrappers/MotifButton';
import MappingModule from '../../../Common/MappingModule/MappingModule';

const CAPTURE_HINT_BORDER_THICKNESS = 10; // pixels
const CAPTURE_HINT_BORDER_COLOR = [199, 21, 133, 255]; // RGBA

let gridApi;

const getColumnDefinition = (analysisDate, setShowModal) => [
	{
		headerName: labels.accounts,
		field: 'accountName',
		cellClass: 'designation-cellrenderer',
		cellRenderer: 'accountNameCellRender',
		sortable: true,
		filter: true,
		width: '300',
		maxWidth: '400'
	},
	{
		headerName: `${labels.currentPeriodBalance} (${analysisDate})`,
		field: 'currentBalance',
		cellRenderer: 'currentYearBalanceCellRender',
		sortable: true,
		filter: true,
		width: '80'
	},
	{
		comparator: (_, __, nodeA, nodeB) => {
			const valueA = getPriorPeriodChange(nodeA.data?.priorBalance, nodeA.data?.currentBalance);
			const valueB = getPriorPeriodChange(nodeB.data?.priorBalance, nodeB.data?.currentBalance);

			if (isNullOrUndefined(valueA)) {
				if (isNullOrUndefined(valueB)) {
					return 0;
				}
				return -1;
			}
			if (isNullOrUndefined(valueB)) {
				return 1;
			}

			if (valueA > valueB) {
				return 1;
			} else {
				if (valueA < valueB) {
					return -1;
				}
			}
			return 0;
		},
		headerName: `${labels.priorPeriodChange} (${analysisDate})`,
		field: 'priorPeriod',
		cellRenderer: 'priorPeriodCellRender',
		sortable: true,
		filter: true,
		width: '80'
	},
	{
		headerName: labels.xTEMateriality,
		field: 'currentBalance',
		cellRenderer: 'xMaterialityCellRender',
		sortable: true,
		filter: true,
		width: '70'
	},
	{
		headerName: labels.estimateAssociated,
		field: 'hasEstimate',
		cellRenderer: 'hasEstimatesCellRender',
		sortable: true,
		filter: true,
		width: '75'
	},
	{
		headerName: labels.designation,
		field: 'accountTypeId',
		cellClass: 'RiskDesignationCellRenderer',
		cellRenderer: 'designationCellRender',
		setShowModal: setShowModal,
		sortable: true,
		filter: true,
		width: '75'
	}
];

const AccountSummaryTable = (props) => {
	const {extendColumns, extendFrameworkComp} = props;
	const /*[manageAccountsModalOpen, setManageAccountsModalOpen] = useState(false),*/
		[firstPrimaryProject, setFirstPrimaryProject] = useState(undefined);
	const {helixJsonModel} = useSelector((state) => {
		return state?.helixProjects.length > 0 ? state?.helixProjects[0] : {};
	});
	const documentId = useDocumentId();
	const userPreferences = useUserPreferences(),
		formData = useSelector((state) => getTaskDocumentByDocumentId(state, documentId)),
		featureConfigurations = useSelector((store) => store.configurations);
	const [showModal, setShowModal] = useModal(),
		[showMappingModuleModal, setShowMappingModuleModal] = useState(false),
		[showMappingModule, setShowMappingModule] = useState(false);
	const accountId = getShownModalEntityId(showModal);

	let columnDefinition = getColumnDefinition(
		helixJsonModel
			? Utility.getFormattedDateValue(userPreferences, helixJsonModel?.analysisDate)
			: labels.notapplicable,
		setShowModal
	);

	if (extendColumns) {
		columnDefinition = extendColumns(columnDefinition);
	}

	let frameworkComponents = {
		agColumnHeader: MotifTableHeaderRenderer,
		hasEstimatesCellRender: HasEstimatesCellRender,
		designationCellRender: DesignationCellRender,
		accountNameCellRender: AccountNameCellRender,
		priorPeriodCellRender: PriorPeriodCellRender,
		AnalyticsCellRender: AnalyticsCellRender,
		currentYearBalanceCellRender: CurrentYearBalanceCellRender,
		xMaterialityCellRender: XMaterialityCellRender
	};

	if (extendFrameworkComp) {
		frameworkComponents = extendFrameworkComp(frameworkComponents);
	}

	const accountIds = useSelector(getAccountsAllIds);
	const accountsById = useSelector((state) => getSliceById(state, 'accounts'));
	let dataRow = [];
	Object.entries(accountsById).forEach(([key, value]) => dataRow.push(value));
	dataRow = Utility.mapOrder(dataRow, accountIds, 'id');
	const handleGridReady = (params) => {
		gridApi = params.api;
		gridApi.sizeColumnsToFit();
	};
	const rowData = useSelector((state) =>
		getAccountsByAccountTypeIds(state, [AccountType.LimitedRisk, AccountType.Significant, AccountType.InSignificant])
	);

	useEffect(() => {
		if (gridApi) {
			gridApi.sizeColumnsToFit();
		}
	});

	useEffect(() => {
		if (featureConfigurations && featureConfigurations.features) {
			setShowMappingModule(featureConfigurations.features.enableHelixMappingModule);
		}
	}, [featureConfigurations]);

	useEffect(() => {
		if (formData?.helixAnalyzers) {
			formData.helixAnalyzers.every((analyzer) => {
				if (analyzer.isPrimaryHelixProject) {
					setFirstPrimaryProject(analyzer);
					return false;
				}
				return true;
			});
		}
	}, [formData]);

	return (
		<StyledAccountSummaryList className="StyledAccountSummaryList">
			<>
				<section className="SectionRighSideWrapper">
					{showMappingModule && (
						<section className="ManageAccountBtn">
							<Button
								variant="text-alt"
								className="manageAccount"
								onClick={() => {
									setShowMappingModuleModal(!showMappingModuleModal);
								}}
								showLabel
								isEllipsed
								buttonLabel={labels.manageAccountMapping}
								noOfLines={1}
							/>
						</section>
					)}
				</section>
				<MotifTableWrapper
					// ref={gridApi}
					columnDefs={columnDefinition}
					rowData={rowData}
					onGridReady={handleGridReady}
					domLayout="autoHeight"
					suppressRowClickSelection
					suppressCellSelection
					suppressHorizontalScroll
					rowBuffer={200}
					rowHeight={60}
					animateRows
					onSortChanged={() => gridApi.redrawRows()}
					overlayNoRowsTemplate={labels.noAccountshasbeenIdentified}
					components={frameworkComponents}
					enableBrowserTooltips
				/>
			</>
			{showModal[accountId] && (
				<EditAccountModalV2
					show={showModal}
					onClose={() => setShowModal(accountId)}
					accountId={accountId}
					callFrom="changeDesignationToSignificant"
				/>
			)}
			{showMappingModuleModal && (
				<MappingModule
					project={firstPrimaryProject}
					showModal={showMappingModuleModal}
					hideMappingModule={setShowMappingModuleModal}
				/>
			)}
		</StyledAccountSummaryList>
	);
};

export default AccountSummaryTable;

const StyledAccountSummaryList = styled.section`
	margin: var(--px-20) 0 var(--px-30) 0;
	.accounts-header {
		.accounts-title {
			font-size: var(--px-20);
			font-weight: 300;
		}
	}
	.SectionRighSideWrapper {
		margin: 0 0 0 auto;
		width: auto;
		max-width: var(--px-420);
		display: flex;
		align-items: center;
		justify-content: flex-end;
	}
	.LinkSeparator {
		margin: 0 var(--px-10);
		color: var(--blue-600);
		font-weight: bold;
		line-height: var(--px-22);
	}
	.ManageAccountBtn {
		display: flex;
		align-items: center;
		width: auto;
		max-width: var(--px-200);
	}
	.motif-table {
		&.AGGridWrapperSection {
			.ag-root-wrapper {
				background: transparent;
				border-color: transparent;
			}
			.ag-header-cell:not(.ag-column-resizing):hover {
				background-color: var(--neutrals-200);
			}
			.ag-header-cell:not(.ag-column-resizing) + .ag-header-cell:not(.ag-header-cell-moving):hover {
				background-color: var(--neutrals-200);
			}
			.ag-paging-panel {
				border-radius: 0 0 var(--px-12) var(--px-12);
			}
		}
		.ag-cell {
			&:focus {
				background: transparent !important;
			}
		}
		.ag-ltr {
			.ag-has-focus {
				.ag-cell-focus:not(.ag-cell-range-selected) {
					border: none;
				}
			}
		}
		.ag-root {
			${(props) => (props.rowData ? `background-color: var(--neutrals-00white)` : '')}
			&.ag-layout-auto-height {
				height: 100%;
			}
		}
		.ag-header {
			border-color: var(--neutrals-200) !important;
			border-right-color: var(--neutrals-200) !important;
			border-bottom-color: var(--neutrals-200) !important;
			border-right: var(--px-1) solid var(--neutrals-200) !important;
			height: var(--px-58) !important;
			border-radius: var(--px-12) var(--px-12) 0 0;
			font-size: var(--px-14);
			font-weight: 400;
			.ag-pinned-left-cols-container,
			.ag-pinned-left-header {
				box-shadow: none;
			}
			.ag-pinned-left-header,
			.ag-header-viewport,
			.ag-header-cell {
				background-color: var(--neutrals-200);
			}
			.ag-header-viewport {
				&:after {
					width: 0 !important;
				}
				.ag-header-container {
					.ag-header-row {
						height: 100% !important;
						.ag-header-cell {
							.motif-table-header-renderer {
								justify-content: flex-end;
								div {
									text-align: right;
									white-space: pre-wrap;
									overflow: hidden;
									text-overflow: ellipsis;
								}
								.motif-table-header-title {
									width: auto;
									max-width: calc(100% - var(--px-20));
								}
								.motif-table-header-icons {
									width: var(--px-20);
									flex-shrink: 0;
									svg {
										height: var(--px-18) !important;
										width: var(--px-18) !important;
									}
								}
							}
							&[col-id='accountName'] {
								padding-left: var(--px-24);
								.motif-table-header-renderer {
									justify-content: flex-start;
								}
							}
							&[col-id='currentBalance'] {
								justify-content: flex-end !important;
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end !important;
								}
								.motif-table-header-renderer {
									justify-content: flex-end !important;
								}
							}
							&[col-id='priorPeriod'] {
								justify-content: flex-end !important;
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end !important;
								}
								.motif-table-header-renderer {
									justify-content: flex-end !important;
								}
							}
							&[col-id='currentBalance_1'] {
								justify-content: flex-end !important;
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end !important;
								}
								.motif-table-header-renderer {
									justify-content: flex-end !important;
								}
							}
							&[col-id='hasEstimate'] {
								justify-content: center !important;
								.ag-header-cell-comp-wrapper {
									justify-content: center !important;
								}
								.motif-table-header-renderer {
									justify-content: center !important;
								}
							}
							&[col-id='accountTypeId'] {
								justify-content: center !important;
								.ag-header-cell-comp-wrapper {
									justify-content: center !important;
								}
								.motif-table-header-renderer {
									justify-content: center !important;
								}
							}
						}
					}
				}
			}
		}
		.ag-body-viewport {
			&.ag-layout-auto-height {
				margin-bottom: 0;
				overflow-y: auto;
				max-height: var(--px-600);
				border-radius: 0 0 var(--px-12) var(--px-12);
				background-color: var(--neutrals-00white);
			}
			&:after {
				width: 0 !important;
			}
			.ag-pinned-left-cols-container {
				margin-left: -1px;
			}
			.ag-row {
				font-weight: normal;
				border: var(--px-1) solid transparent !important;
				&.ag-row-hover {
					font-weight: normal;
					background: var(--neutrals-25) !important;
					border: var(--px-1) solid transparent !important;
					& > .ag-column-hover {
						background: transparent !important;
					}
				}
				&.ag-row-first {
					font-weight: normal;
					background: var(--neutrals-00white) !important;
				}
				&.ag-row-even,
				&.ag-row-odd {
					background: var(--neutrals-00white) !important;
					&.ag-row-hover {
						background: transparent !important;
					}
				}
				&.ag-row-odd {
					background: var(--neutrals-25) !important;
					&.ag-row-hover {
						background: var(--neutrals-25) !important;
					}
				}
				&.ag-row-last {
					border-radius: 0 0 var(--px-12) var(--px-12);
				}
				.ag-row-selected {
					background-color: var(--neutrals-100) !important;
					&.ag-row-even,
					&.ag-row-odd {
						background-color: var(--neutrals-100) !important;
					}
				}
				.ag-cell {
					&[col-id='accountName'] {
						padding-left: var(--px-24);
					}
					&[col-id='hasEstimate'] {
						justify-content: center !important;
						.hasestimates-cellrenderer {
							justify-content: center !important;
						}
					}
					&[col-id='accountTypeId'] {
						justify-content: center !important;
						.DesignationBodyCell {
							justify-content: center !important;
							width: inherit;
							.StyledAccountDesignationDropdown {
								width: var(--px-150);
							}
						}
					}
				}
			}
		}
		.ag-overlay {
			.ag-overlay-panel {
				.ag-overlay-wrapper {
					font-style: normal;
					font-weight: 300;
					font-size: var(--px-16);
					line-height: var(--px-24);
					color: var(--neutrals-300);
					.ag-overlay-no-rows-wrapper {
						border: var(--px-1) solid var(--neutrals-200);
						border-radius: 0 0 var(--px-12) var(--px-12);
					}
				}
			}
		}
		.ag-center-cols-viewport {
			overflow-x: hidden;
		}
		input[class^='ag-'][type='button']:focus,
		button:focus {
			box-shadow: none !important;
		}
		.motif-table-end-component-renderer {
			overflow: hidden;
			text-overflow: ellipsis;
			display: block;
		}
		.ag-cell,
		.ag-header-cell {
			font-weight: 400;
			font-size: var(--px-14);
			line-height: var(--px-22);
			padding: 0 var(--px-5);
		}
	}
	.accsummary-cellrenderer {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		svg {
			width: var(--px-18) !important;
			height: var(--px-18) !important;
			fill: var(--neutrals-900);
			path {
				fill: var(--neutrals-900);
			}
		}
	}
	.priorperiod-cellrenderer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		height: 100%;
		.analytics-icon-wrapper {
			margin-left: var(--px-30);
		}
		svg {
			fill: var(--neutrals-900);
			path {
				fill: var(--neutrals-900);
			}
		}
	}
	.accounts-chart {
		margin-bottom: var(--px-15);
		svg {
			cursor: default !important;
		}
	}
	.accountsdropdown-wrap {
		width: 30%;
		margin-top: var(--px-20);
		.motif-select-trigger {
			height: var(--px-46);
		}
		.motif-select-arrow {
			width: var(--px-24);
			height: var(--px-24);
			top: var(--px-12);
			right: var(--px-12);
			margin-left: var(--px-16);
		}
	}
	.accounts-count {
		font-size: var(--px-14);
		font-weight: 400;
	}
`;

const StyledModalFooter = styled.footer`
	padding: var(--px-10) var(--px-24);
	display: flex;
	border-top: var(--px-1) solid var(--neutrals-200);
	button {
		margin-right: var(--px-20);
		.ellipses {
			div {
				text-align: center;
				width: 100%;
			}
		}
	}
`;

const StyledIframeContainer = styled.section`
	padding: var(--px-24);
	height: 100%;

	&.with-capture-border {
		position: relative;

		&::before {
			content: '';
			display: block;
			position: absolute;
			z-index: 1;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			margin: var(--px-16);
			border: ${CAPTURE_HINT_BORDER_THICKNESS}rem solid rgb(${CAPTURE_HINT_BORDER_COLOR.slice(0, 3).join(',')});
		}
	}

	.HelixErrorMessage {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		font-size: var(--px-24);
		font-weight: 300;
		line-height: var(--px-32);
		color: var(--neutrals-700);
	}
`;
